import React, { useRef, useMemo, useState, useEffect, Suspense, forwardRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { LineChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { rgbaColor } from '../../../../../helpers/utils';
import { useAppContext } from '../../../../../providers/AppProvider';
import { Spinner } from 'react-bootstrap';
import alertasFiltrosStore from '../../../../../lib/store/alertas_filtros_store';

const seriesNames = ['Período Actual', 'Período Anterior'];
// Carga diferida del componente ReactEchart con prioridad
const ReactEchart = React.lazy(() =>
    new Promise(resolve => {
        setTimeout(() => {
            resolve(import('../../../../../components/common/ReactEchart'));
        }, 100);
    })
);

// Registrar solo los componentes necesarios
echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    LineChart,
    LegendComponent,
    CanvasRenderer
]);

// Extraer la función de formato del tooltip fuera del componente
const tooltipFormatter = params => {
    let tooltip = `<div>${params[0].name}</div>`

    tooltip += params.map(({ value, borderColor, seriesName }) =>
        `<div class="dot me-1 fs-11 d-inline-block" style="background-color: ${borderColor}"></div>
         <span class='text-600'>${seriesName}: ${value}</span>`
    ).join('<br/>');

    return tooltip;
};



// Extraer la función getOptions fuera del componente para evitar recreaciones
const getOptions = (getThemeColor, data, isSingleDay, dataBefore, store) => {





    const defaultOptions = {
        color: getThemeColor('gray-100'),
        tooltip: {
            trigger: 'axis',
            padding: [7, 10],
            axisPointer: { type: 'shadow' },
            backgroundColor: getThemeColor('gray-100'),
            borderColor: getThemeColor('gray-100'),
            textStyle: { color: getThemeColor('gray-1100') },
            borderWidth: 1,
            formatter: params => {
                if (!params || params.length === 0) return 'No hay datos';

                const label = params[0].name;
                // ✅ MEJORAR EL TOOLTIP PARA MOSTRAR INFORMACIÓN MÁS CLARA
                let tooltip = `<div><strong>`;

                /*
                if (isSingleDay) {
                    // Para un solo día, mostrar la hora
                    tooltip += `Hora: ${label}`;
                } else {
                    // Para múltiples días, mostrar el día con contexto
                    tooltip += `Día ${label}`;
                }

                tooltip += `</strong></div>`;
                */
                tooltip += params.map(({ value, borderColor, seriesName }) => {
                    let format = 'DD/MM/YYYY';
                    let label = seriesName === seriesNames[0] ? dayjs(store.fechaInicio).format(format) || seriesName :
                        seriesName === seriesNames[1] ? dayjs(store.fechaInicioBefore).format(format) || seriesName : seriesName;
                    return `<div class="dot me-1 fs-11 d-inline-block" style="background-color: ${borderColor}"></div>
             <span class='text-600'>${isSingleDay ? (label + ' ' + hour) : label} - <strong>${value}</strong> alertas</span>`;
                }).join('<br/>');

                return tooltip || '';
            },
            transitionDuration: 0
        },
        legend: {
            data: seriesNames,
            show: false,
            top: 0,
            textStyle: {
                color: getThemeColor('gray-600')
            }
        },
        animation: false,
        xAxis: {
            type: 'category',
            data: ['Sin datos'],
            boundaryGap: false,
            axisLabel: {
                color: getThemeColor('gray-400'),
                margin: 15
            },
            axisTick: { show: false },
            axisLine: { show: false }
        },
        yAxis: {
            type: 'value',
            axisPointer: { show: false },
            splitLine: {
                lineStyle: {
                    color: getThemeColor('gray-300'),
                    type: 'dashed'
                }
            },
            boundaryGap: false,
            axisLabel: {
                show: true,
                color: getThemeColor('gray-400'),
                margin: 15
            },
            axisTick: { show: false },
            axisLine: { show: false }
        },
        series: [
            {
                name: 'Período Actual',
                type: 'line',
                data: [0],
                lineStyle: {
                    color: getThemeColor('primary'),
                    width: 3
                },
                symbol: 'circle',
                symbolSize: 8
            },
            {
                name: 'Período Anterior',
                type: 'line',
                data: [0],
                lineStyle: {
                    color: rgbaColor(getThemeColor('warning'), 0.8),
                    width: 2,
                    type: 'dashed'
                },
                symbol: 'diamond',
                symbolSize: 6
            }
        ],
        grid: {
            right: '18px',
            left: '50px',
            bottom: '15%',
            top: '15%'
        }
    };

    // ✅ VALIDAR getThemeColor ANTES DE PROCEDER
    if (!getThemeColor || typeof getThemeColor !== 'function') {
        console.warn('⚠️ getThemeColor no está disponible, usando opciones básicas');
        return {
            ...defaultOptions,
            color: '#6c757d',
            tooltip: { ...defaultOptions.tooltip, backgroundColor: '#f8f9fa' }
        };
    }


    // ✅MAPEO DE DATOS - MANEJAR AMBAS ESTRUCTURAS
    const safeData = (data || []).map(item => ({
        fecha: item.fecha || item.date || '',
        hora: item.hora || item.hour || '',
        cantidad: item.cantidad !== undefined ? item.cantidad :
            (item.count !== undefined ? item.count : 0),
    }));

    const safeDataBefore = (dataBefore || []).map(item => ({
        fecha: item.fecha || item.date || '',
        hora: item.hora || item.hour || '',
        cantidad: item.cantidad !== undefined ? item.cantidad :
            (item.count !== undefined ? item.count : 0),
    }));




    //console.log('🔍 Datos después del mapeo - Actuales:', safeData.slice(0, 3));
    //console.log('🔍 Datos después del mapeo - Anteriores:', safeDataBefore.slice(0, 3));

    // ✅ SI NO HAY DATOS VÁLIDOS, RETORNAR OPCIONES POR DEFECTO
    if (safeData.length === 0) {
        console.warn('⚠️ No hay datos válidos para el período actual, usando opciones por defecto');
        return defaultOptions;
    }

    // ✅ CREAR EJE X UNIFICADO Y ALINEAR DATOS
    let keyMaster = [];
    safeData.forEach((item, index) => {
        var key = item.fecha || item.hora;
        if (safeDataBefore.length > 0) {
            `${item.fecha || item.hora} - ${safeDataBefore[index]?.fecha || safeDataBefore[index]?.hora}`;

        }
        keyMaster.push(key);
    });

    const currentLabels = keyMaster.map(item => item).filter(Boolean);
    const beforeLabels = keyMaster.map(item => item).filter(Boolean);

    if (currentLabels.length === 0) {
        console.warn('⚠️ No hay etiquetas válidas para el eje X');
        return defaultOptions;
    }

    //console.log('🏷️ Etiquetas del eje X - Actuales:', currentLabels.slice(0, 5));
    //console.log('🏷️ Etiquetas del eje X - Anteriores:', beforeLabels.slice(0, 5));

    // Crear un mapa de datos anteriores para fácil búsqueda
    const beforeDataMap = new Map();
    safeDataBefore.forEach((item, index) => {
        const key = keyMaster[index];
        if (key) {  // ✅ Solo agregar si la clave es válida
            beforeDataMap.set(key, item.cantidad);
        }
    });

    // ✅ SI HAY FECHAS EN EL PERÍODO ANTERIOR QUE NO ESTÁN EN EL ACTUAL, INCLUIRLAS
    const allLabels = [...new Set([...currentLabels, ...beforeLabels])].sort();

    //console.log('🎯 Todas las etiquetas unificadas:', allLabels.slice(0, 10));

    // Reajustar ambos datasets para incluir todas las fechas
    const currentDataMap = new Map();
    safeData.forEach((item, index) => {
        const key = keyMaster[index];
        if (key) {  // ✅ Solo agregar si la clave es válida
            currentDataMap.set(key, item.cantidad);
        }
    });

    const alignedCurrentData = allLabels.map(label => currentDataMap.get(label) || 0);
    const alignedBeforeDataFull = allLabels.map(label => beforeDataMap.get(label) || 0);

    // ✅ VERIFICAR LOS DATOS FINALES
    //console.log('📈 Datos alineados - Actual:', alignedCurrentData.slice(0, 5));
    //console.log('📈 Datos alineados - Anterior:', alignedBeforeDataFull.slice(0, 5));
    //console.log('📊 Suma total - Actual:', alignedCurrentData.reduce((a, b) => a + b, 0));
    //console.log('📊 Suma total - Anterior:', alignedBeforeDataFull.reduce((a, b) => a + b, 0));

    return {
        color: getThemeColor('gray-100'),
        tooltip: {
            trigger: 'axis',
            padding: [7, 10],
            axisPointer: { type: 'shadow' },
            backgroundColor: getThemeColor('gray-100'),
            borderColor: getThemeColor('gray-100'),
            textStyle: { color: getThemeColor('gray-1100') },
            borderWidth: 1,
            formatter: params => {
                if (!params || params.length === 0) return 'No hay datos';

                const hour = params[0].name;

                // ✅ TOOLTIP CORREGIDO
                let tooltip = `<div><strong>`;
                /*
                                if (isSingleDay) {
                                    // Para un solo día, mostrar la hora
                                    tooltip += `Hora: ${label}`;
                                } else {
                                    // Para múltiples días, verificar el tipo de etiqueta
                                    if (/^\d{1,2}$/.test(label)) {
                                        // Es solo un número de día
                                        tooltip += `Día ${label}`;
                                    } else if (label.includes('-') && dayjs(label).isValid()) {
                                        // Es una fecha completa
                                        tooltip += `${dayjs(label).format('DD [de] MMM')}`;
                                    } else {
                                        // Fallback
                                        tooltip += `Día ${label}`;
                                    }
                                }
                tooltip += `</strong></div>`;
                */


                tooltip += params.map(({ value, borderColor, seriesName }) => {
                    let format = 'DD/MM/YYYY';
                    let label = seriesName === seriesNames[0] ? dayjs(store.fechaInicio).format(format) || seriesName :
                        seriesName === seriesNames[1] ? dayjs(store.fechaInicioBefore).format(format) || seriesName : seriesName;
                    return `<div class="dot me-1 fs-11 d-inline-block" style="background-color: ${borderColor}"></div>
             <span class='text-600'>${isSingleDay ? (label + ' ' + hour) : label} - <strong>${value}</strong> alertas</span>`;
                }).join('<br/>');

                return tooltip || '';
            },
            transitionDuration: 0
        },
        legend: {
            data: seriesNames,
            show: false,
            top: 0,
            textStyle: {
                color: getThemeColor('gray-600')
            }
        },
        animation: true,
        xAxis: {
            type: 'category',
            data: allLabels, // ✅ Usar todas las fechas/horas válidas
            boundaryGap: false,
            axisPointer: {
                lineStyle: {
                    color: getThemeColor('gray-300'),
                    type: 'dashed'
                }
            },
            splitLine: { show: false },
            axisLine: {
                lineStyle: {
                    color: rgbaColor('#000', 0.01),
                    type: 'dashed'
                }
            },
            axisTick: { show: false },
            axisLabel: {
                color: getThemeColor('gray-400'),
                margin: 15,
                formatter: value => {
                    // ✅ FORMATTER CORREGIDO PARA MANEJAR DIFERENTES TIPOS DE ETIQUETAS
                    if (!value) return '';

                    if (isSingleDay) {
                        // Para un solo día, las etiquetas son horas: "14:00", "15:00"
                        return `${value}`;
                    } else {
                        // Para múltiples días, las etiquetas pueden ser:
                        // - Números de día: "19", "20", "21"
                        // - Fechas completas: "2025-05-19"

                        // Si es solo un número (día), mostrarlo tal como está
                        if (/^\d{1,2}$/.test(value)) {
                            return `${value}`;
                        }

                        // Si parece una fecha completa, formatearla
                        if (value.includes('-') && dayjs(value).isValid()) {
                            return dayjs(value).format('MMM DD');
                        }

                        // Fallback: mostrar tal como viene
                        return `${value}`;
                    }
                },
                interval: 0,
                rotate: allLabels.length > 12 ? 45 : 0
            }
        },
        yAxis: {
            type: 'value',
            axisPointer: { show: false },
            splitLine: {
                lineStyle: {
                    color: getThemeColor('gray-300'),
                    type: 'dashed'
                }
            },
            boundaryGap: false,
            axisLabel: {
                show: true,
                color: getThemeColor('gray-400'),
                margin: 15
            },
            axisTick: { show: false },
            axisLine: { show: false }
        },
        series: [
            {
                name: 'Período Actual',
                type: 'line',
                data: alignedCurrentData,
                lineStyle: {
                    color: getThemeColor('primary'),
                    width: 3
                },
                itemStyle: {
                    borderColor: getThemeColor('primary'),
                    borderWidth: 2,
                    backgroundColor: "transparent",
                },
                symbol: 'circle',
                symbolSize: 8,
                emphasis: {
                    scale: true
                },
                areaStyle: {
                    color: {
                        type: "linear",
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            {
                                offset: 0,
                                color: rgbaColor(getThemeColor("primary"), 0.2),
                            },
                            {
                                offset: 1,
                                color: rgbaColor(getThemeColor("primary"), 0),
                            },
                        ],
                    },
                },
            },
            {
                name: 'Período Anterior',
                type: 'line',
                data: alignedBeforeDataFull,
                lineStyle: {
                    color: rgbaColor(getThemeColor('warning'), 1),
                    width: 3
                },
                itemStyle: {
                    borderColor: rgbaColor(getThemeColor('warning'), 1),
                    borderWidth: 2,
                    backgroundColor: "transparent",
                },
                symbol: 'circle',
                symbolSize: 8,
                emphasis: {
                    scale: true
                },
                areaStyle: {
                    color: {
                        type: "linear",
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            {
                                offset: 0,
                                color: rgbaColor(getThemeColor("primary"), 0.2),
                            },
                            {
                                offset: 1,
                                color: rgbaColor(getThemeColor("primary"), 0),
                            },
                        ],
                    },
                },
            },
        ],
        grid: {
            right: '28px',
            left: '70px',
            bottom: isSingleDay ? '20%' : '20%',
            top: '15%'
        }
    };
};

// const getOptions = (getThemeColor, data, isSingleDay, dataBefore) => {
//     // Validación de datos y mapeo flexible para evitar errores
//     const safeData = (data || []).map(item => ({
//         fecha: item.fecha || item.date || '',  // ✅ Manejar ambas propiedades
//         hora: item.hora || item.hour || '',
//         cantidad: item.cantidad !== undefined ? item.cantidad : 
//                  (item.count !== undefined ? item.count : 0)  // ✅ Manejar count
//     }));

//     const safeDataBefore = (dataBefore || []).map(item => ({
//         fecha: item.fecha || item.date || '',  // ✅ Manejar ambas propiedades
//         hora: item.hora || item.hour || '',
//         cantidad: item.cantidad !== undefined ? item.cantidad : 
//                  (item.count !== undefined ? item.count : 0)  // ✅ Manejar count
//     }));

//     // ✅ DEBUG PARA VERIFICAR LA ESTRUCTURA
//     console.log('🔍 Datos después del mapeo - Actuales:', safeData.slice(0, 3));
//     console.log('🔍 Datos después del mapeo - Anteriores:', safeDataBefore.slice(0, 3));


//     if (safeData.length === 0) {
//         console.warn('⚠️ No hay datos válidos para el período actual');
//         return null;
//     }

//     // ✅ CREAR EJE X UNIFICADO Y ALINEAR DATOS
//     const currentLabels = safeData.map(item => item.fecha || item.hora);
//     const beforeLabels = safeDataBefore.map(item => item.fecha || item.hora);

//     // Crear un mapa de datos anteriores para fácil búsqueda
//     const beforeDataMap = new Map();
//     safeDataBefore.forEach(item => {
//         const key = item.fecha || item.hora;
//         beforeDataMap.set(key, item.cantidad);
//     });

//     // Alinear datos del período anterior con las fechas del actual
//     const alignedBeforeData = currentLabels.map(label => {
//         return beforeDataMap.get(label) || 0; // 0 si no hay datos para esa fecha/hora
//     });

//     // ✅ SI HAY FECHAS EN EL PERÍODO ANTERIOR QUE NO ESTÁN EN EL ACTUAL, INCLUIRLAS
//     const allLabels = [...new Set([...currentLabels, ...beforeLabels])].sort();

//     // Reajustar ambos datasets para incluir todas las fechas
//     const currentDataMap = new Map();
//     safeData.forEach(item => {
//         const key = item.fecha || item.hora;
//         currentDataMap.set(key, item.cantidad);
//     });

//     const alignedCurrentData = allLabels.map(label => currentDataMap.get(label) || 0);
//     const alignedBeforeDataFull = allLabels.map(label => beforeDataMap.get(label) || 0);

//     return {
//         color: getThemeColor('gray-100'),
//         tooltip: {
//             trigger: 'axis',
//             padding: [7, 10],
//             backgroundColor: getThemeColor('gray-100'),
//             borderColor: getThemeColor('gray-100'),
//             textStyle: { color: getThemeColor('gray-1100') },
//             borderWidth: 1,
//             formatter: params => {
//                 let tooltip = `<div><strong>${params[0].name}</strong></div>`;

//                 tooltip += params.map(({ value, borderColor, seriesName }) =>
//                     `<div class="dot me-1 fs-11 d-inline-block" style="background-color: ${borderColor}"></div>
//                      <span class='text-600'>${seriesName}: <strong>${value}</strong></span>`
//                 ).join('<br/>');

//                 return tooltip;
//             },
//             transitionDuration: 0
//         },
//         legend: { 
//             data: ['Período Actual', 'Período Anterior'], 
//             show: true,
//             top: 0,
//             textStyle: {
//                 color: getThemeColor('gray-600')
//             }
//         },
//         animation: false,
//         xAxis: {
//             type: 'category',
//             data: allLabels, // ✅ Usar todas las fechas/horas
//             boundaryGap: false,
//             axisPointer: {
//                 lineStyle: {
//                     color: getThemeColor('gray-300'),
//                     type: 'dashed'
//                 }
//             },
//             splitLine: { show: false },
//             axisLine: {
//                 lineStyle: {
//                     color: rgbaColor('#000', 0.01),
//                     type: 'dashed'
//                 }
//             },
//             axisTick: { show: false },
//             axisLabel: {
//                 color: getThemeColor('gray-400'),
//                 margin: 15,
//                 formatter: value => isSingleDay ? `${value}` : dayjs(value).format('MMM DD'),
//                 interval: 0,
//                 rotate: isSingleDay ? 0 : 45 // Rotar las fechas si no es un solo día
//             }
//         },
//         yAxis: {
//             type: 'value',
//             axisPointer: { show: false },
//             splitLine: {
//                 lineStyle: {
//                     color: getThemeColor('gray-300'),
//                     type: 'dashed'
//                 }
//             },
//             boundaryGap: false,
//             axisLabel: {
//                 show: true,
//                 color: getThemeColor('gray-400'),
//                 margin: 15
//             },
//             axisTick: { show: false },
//             axisLine: { show: false }
//         },
//         series: [
//             {
//                 name: 'Período Actual',
//                 type: 'line',
//                 data: alignedCurrentData, // ✅ Datos alineados del período actual
//                 lineStyle: { 
//                     color: getThemeColor('primary'),
//                     width: 3
//                 },
//                 itemStyle: {
//                     borderColor: getThemeColor('primary'),
//                     borderWidth: 2,
//                     backgroundColor: "transparent",
//                 },
//                 symbol: 'circle',
//                 symbolSize: 8,
//                 emphasis: {
//                     scale: true
//                 },
//                 areaStyle: {
//                     color: {
//                         type: "linear",
//                         x: 0,
//                         y: 0,
//                         x2: 0,
//                         y2: 1,
//                         colorStops: [
//                             {
//                                 offset: 0,
//                                 color: rgbaColor(getThemeColor("primary"), 0.2),
//                             },
//                             {
//                                 offset: 1,
//                                 color: rgbaColor(getThemeColor("primary"), 0),
//                             },
//                         ],
//                     },
//                 },
//             },
//             {
//                 name: 'Período Anterior',
//                 type: 'line',
//                 data: alignedBeforeDataFull, // ✅ Datos alineados del período anterior
//                 lineStyle: { 
//                     color: rgbaColor(getThemeColor('warning'), 0.8),
//                     width: 2,
//                     type: 'dashed' // Línea punteada para diferenciar
//                 },
//                 itemStyle: {
//                     borderColor: rgbaColor(getThemeColor('warning'), 0.8),
//                     borderWidth: 2,
//                     backgroundColor: "transparent",
//                 },
//                 symbol: 'diamond',
//                 symbolSize: 6,
//                 emphasis: {
//                     scale: true
//                 }
//             }
//         ],
//         grid: { 
//             right: '18px', 
//             left: '50px', 
//             bottom: isSingleDay ? '15%' : '25%', // Más espacio si las fechas están rotadas
//             top: '15%' 
//         }
//     };
// };

// Estados de carga más granulares para mejor feedback
const LOADING_STATES = {
    INITIAL: 'initial',
    DATA_READY: 'data_ready',
    RENDERING: 'rendering',
    COMPLETED: 'completed'
};

// Envolver el componente con forwardRef para manejar correctamente las refs
const AlertaFrecuenciaChart = forwardRef(({ data, isSingleDay, dataBefore }, ref) => {
    const store = alertasFiltrosStore();
    const chartRef = useRef(null);
    const { getThemeColor } = useAppContext() || {};
    const [loadingState, setLoadingState] = useState(LOADING_STATES.INITIAL);
    const [lastRenderTime, setLastRenderTime] = useState(0);

    // Validación de datos para evitar errores
    const validData = Array.isArray(data) ? data : [];
    const validDataBefore = Array.isArray(dataBefore) ? dataBefore : [];

    // Memoización de datos para evitar recreaciones innecesarias
    const dataSignature = useMemo(() => {
        const dataString = validData.map(d => {
            const x = d.fecha || d.date || d.hora || d.hour || '';
            const y = d.cantidad !== undefined ? d.cantidad : (d.count !== undefined ? d.count : 0);
            return `${x}:${y}`;
        }).join('|');

        const beforeString = validDataBefore.map(d => {
            const x = d.fecha || d.date || d.hora || d.hour || '';
            const y = d.cantidad !== undefined ? d.cantidad : (d.count !== undefined ? d.count : 0);
            return `${x}:${y}`;
        }).join('|');

        return `${dataString}||${beforeString}`;
    }, [validData, validDataBefore]);

    // Limitar la cantidad de puntos para mejorar rendimiento
    // const processedData = useMemo(() => {
    //     if (validData.length === 0) return [];

    //     // Si son menos de 60 puntos, usar todos
    //     if (validData.length <= 60) return validData;

    //     // Para grandes volúmenes, tomar muestras regulares
    //     const step = Math.ceil(validData.length / 60);
    //     return validData.filter((_, index) => index % step === 0);
    // }, [dataSignature]);

    // const processedDataBefore = useMemo(() => {
    //     if (validDataBefore.length === 0) return [];

    //     if (validDataBefore.length <= 60) return validDataBefore;

    //     const step = Math.ceil(validDataBefore.length / 60);
    //     return validDataBefore.filter((_, index) => index % step === 0);
    // }, [dataSignature]);

    const processedData = useMemo(() => {
        //console.log('📊 Procesando datos de frecuencia - Período actual:', validData.length, 'puntos');

        // ✅ Los datos del backend ya vienen agrupados por fecha/hora
        // No necesitamos limitarlos, solo validarlos
        return validData.map(item => ({
            fecha: item.fecha || item.date || '',
            hora: item.hora || item.hour || '',
            cantidad: item.cantidad !== undefined ? item.cantidad : (item.count !== undefined ? item.count : 0),
        }));
    }, [validData]);

    const processedDataBefore = useMemo(() => {
        //console.log('📊 Procesando datos de frecuencia - Período anterior:', validDataBefore.length, 'puntos');

        // ✅ Los datos del backend ya vienen agrupados por fecha/hora
        return validDataBefore.map(item => ({
            fecha: item.fecha || item.date || '',
            hora: item.hora || item.hour || '',
            cantidad: item.cantidad !== undefined ? item.cantidad : (item.count !== undefined ? item.count : 0),
        }));
    }, [validDataBefore]);


    useEffect(() => {
        //console.log("📊 AlertaFrecuenciaChart recibió datos:",
        //    validData.length, "registros para período actual,",
        //    validDataBefore.length, "registros para período anterior");

        // ✅ DEBUG MEJORADO: Verificar estructura de datos
        if (validData.length > 0) {
            //console.log("📊 Estructura de datos actuales:", validData.slice(0, 2));
            const totalActual = validData.reduce((sum, item) => sum + (item.cantidad || item.count || 0), 0);
            //console.log("📊 Total alertas período actual:", totalActual);
        }

        if (validDataBefore.length > 0) {
            //console.log("📊 Estructura de datos anteriores:", validDataBefore.slice(0, 2));
            const totalAnterior = validDataBefore.reduce((sum, item) => sum + (item.cantidad || item.count || 0), 0);
            //console.log("📊 Total alertas período anterior:", totalAnterior);
        }

        if (validData.length > 0 && loadingState === LOADING_STATES.INITIAL) {
            setLoadingState(LOADING_STATES.DATA_READY);
        }
    }, [validData, validDataBefore, loadingState]);


    // Memoizar las opciones del gráfico para evitar recálculos
    // const chartOptions = useMemo(() => 
    //     getOptions(getThemeColor, processedData, isSingleDay, processedDataBefore), 
    //     [getThemeColor, processedData, processedDataBefore, isSingleDay]
    // );
    const chartOptions = useMemo(() =>
        getOptions(getThemeColor, processedData, isSingleDay, processedDataBefore, store),
        [getThemeColor, processedData, processedDataBefore, isSingleDay]
    );

    // Detectar cuando los datos están listos
    useEffect(() => {
        //console.log("📊 AlertaFrecuenciaChart recibió datos:",
        //    validData.length, "registros para período actual,",
        //    validDataBefore.length, "registros para período anterior");

        if (validData.length > 0 && loadingState === LOADING_STATES.INITIAL) {
            setLoadingState(LOADING_STATES.DATA_READY);
        }
    }, [validData, validDataBefore, loadingState]);

    // Prevenir actualizaciones demasiado frecuentes (throttling)
    useEffect(() => {
        if (loadingState === LOADING_STATES.DATA_READY) {
            const now = Date.now();
            // Solo actualizar si han pasado al menos 300ms desde la última renderización
            if (now - lastRenderTime > 300) {
                setLoadingState(LOADING_STATES.RENDERING);
                setLastRenderTime(now);
                // Programar el cambio a COMPLETED después de un breve tiempo
                const timer = setTimeout(() => {
                    setLoadingState(LOADING_STATES.COMPLETED);
                }, 100);
                return () => clearTimeout(timer);
            }
        }
    }, [loadingState, lastRenderTime]);

    // Render lógica con estado de carga
    if (loadingState === LOADING_STATES.INITIAL) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '20rem' }}>
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">Inicializando gráfico de frecuencia...</span>
            </div>
        );
    }

    if (loadingState === LOADING_STATES.DATA_READY) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '20rem' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p className="mb-0 mt-2">Preparando visualización de frecuencia...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ height: '14.325rem' }}>
            <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <span className="ms-2">Cargando biblioteca de gráficos...</span>
                </div>
            }>
                <ReactEchart
                    ref={ref}
                    echarts={echarts}
                    option={chartOptions}
                    style={{ height: '14.325rem', width: '100%' }}
                    notMerge={true}
                    lazyUpdate={true}
                    opts={{
                        renderer: 'canvas',
                        devicePixelRatio: window.devicePixelRatio || 1
                    }}
                />
            </Suspense>
        </div>
    );
});

AlertaFrecuenciaChart.displayName = 'AlertaFrecuenciaChart';

AlertaFrecuenciaChart.propTypes = {
    // Definir props con allowance para ambas estructuras de datos
    data: PropTypes.arrayOf(
        PropTypes.shape({
            fecha: PropTypes.string,
            date: PropTypes.string,
            hora: PropTypes.string,
            hour: PropTypes.string,
            cantidad: PropTypes.number,
            count: PropTypes.number
        })
    ),
    dataBefore: PropTypes.arrayOf(
        PropTypes.shape({
            fecha: PropTypes.string,
            date: PropTypes.string,
            hora: PropTypes.string,
            hour: PropTypes.string,
            cantidad: PropTypes.number,
            count: PropTypes.number
        })
    ),
    isSingleDay: PropTypes.bool,
};

// Valores predeterminados para evitar errores
AlertaFrecuenciaChart.defaultProps = {
    data: [],
    dataBefore: [],
    isSingleDay: false,
};

// Implementación avanzada de memoización con comparación profunda
const arePropsEqual = (prevProps, nextProps) => {
    // Verificar flags escalares
    if (prevProps.isSingleDay !== nextProps.isSingleDay) return false;

    // Si la cantidad de datos es diferente, definitivamente son diferentes
    if (
        (prevProps.data?.length || 0) !== (nextProps.data?.length || 0) ||
        (prevProps.dataBefore?.length || 0) !== (nextProps.dataBefore?.length || 0)
    ) return false;

    // Si no hay datos en ninguno de los dos casos, son iguales
    if (!prevProps.data?.length && !nextProps.data?.length) return true;

    // Función para extraer valores relevantes independiente de la estructura
    const extractDataPoint = d => ({
        x: d.fecha || d.date || d.hora || d.hour || '',
        y: d.cantidad !== undefined ? d.cantidad : (d.count !== undefined ? d.count : 0)
    });

    // Comparación simplificada de datos actuales
    try {
        const prevDataString = JSON.stringify(
            (prevProps.data || []).map(extractDataPoint)
        );

        const nextDataString = JSON.stringify(
            (nextProps.data || []).map(extractDataPoint)
        );

        if (prevDataString !== nextDataString) return false;

        // Comparación simplificada de datos anteriores
        const prevBeforeString = JSON.stringify(
            (prevProps.dataBefore || []).map(extractDataPoint)
        );

        const nextBeforeString = JSON.stringify(
            (nextProps.dataBefore || []).map(extractDataPoint)
        );

        return prevBeforeString === nextBeforeString;
    } catch (error) {
        console.error("Error en comparación de props:", error);
        return false; // En caso de error, renderizar para estar seguros
    }
};

// Exportar componente con memoización avanzada
export default React.memo(AlertaFrecuenciaChart, arePropsEqual);